/**
 * VOY-73: Test page for booking status and error components
 * 
 * This page displays all possible booking statuses and error states
 * for visual testing and verification.
 */

import { useState } from 'react'
import { BookingStatusCard } from '@/components/booking/BookingStatusCard'
import { BookingErrorCard } from '@/components/booking/BookingErrorCard'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { BookingStatus, ErrorCode } from '@/constants/ratehawkStatusCodes'

const ALL_STATUSES: BookingStatus[] = [
  'new',
  'pending',
  'confirmed',
  'cancelled',
  'failed',
  'modified',
  'on_request',
  'processing',
  'timeout',
  'unknown'
]

const ALL_ERRORS: ErrorCode[] = [
  'unavailable',
  'price_changed',
  'invalid_request',
  'payment_failed',
  'timeout',
  'network_error',
  'rate_limit',
  'invalid_credentials',
  'booking_not_found',
  'cancellation_failed',
  'refund_failed',
  'unknown_error'
]

export default function TestBookingStatusPage() {
  const [selectedStatus, setSelectedStatus] = useState<BookingStatus>('confirmed')
  const [selectedError, setSelectedError] = useState<ErrorCode>('payment_failed')
  const [showStatus, setShowStatus] = useState(true)
  const [showError, setShowError] = useState(false)

  const handleRetry = () => {
    console.log('Retry clicked')
    alert('Retry functionality would be implemented here')
  }

  return (
    <div className="container mx-auto py-12">
      <div className="max-w-4xl mx-auto space-y-8">
        <Card>
          <CardHeader>
            <CardTitle>VOY-73: Booking Status & Error Testing</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Status Selector */}
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <Button
                  variant={showStatus ? 'default' : 'outline'}
                  onClick={() => {
                    setShowStatus(true)
                    setShowError(false)
                  }}
                >
                  Show Status
                </Button>
                <Button
                  variant={showError ? 'default' : 'outline'}
                  onClick={() => {
                    setShowStatus(false)
                    setShowError(true)
                  }}
                >
                  Show Error
                </Button>
              </div>

              {showStatus && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Select Booking Status:</label>
                  <Select
                    value={selectedStatus}
                    onValueChange={(value) => setSelectedStatus(value as BookingStatus)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ALL_STATUSES.map((status) => (
                        <SelectItem key={status} value={status}>
                          {status}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {showError && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Select Error Code:</label>
                  <Select
                    value={selectedError}
                    onValueChange={(value) => setSelectedError(value as ErrorCode)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ALL_ERRORS.map((error) => (
                        <SelectItem key={error} value={error}>
                          {error}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Status Display */}
        {showStatus && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold">Booking Status: {selectedStatus}</h2>
            <BookingStatusCard
              status={selectedStatus}
              orderId="TEST-ORDER-12345"
              onRetry={handleRetry}
            />
          </div>
        )}

        {/* Error Display */}
        {showError && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold">Error Code: {selectedError}</h2>
            <BookingErrorCard
              error={`Test error for ${selectedError}`}
              errorCode={selectedError}
              onRetry={handleRetry}
            />
          </div>
        )}

        {/* All Statuses Grid */}
        <Card>
          <CardHeader>
            <CardTitle>All Booking Statuses (Quick Preview)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {ALL_STATUSES.map((status) => (
                <div key={status} className="border rounded-lg p-4">
                  <h3 className="font-semibold mb-2">{status}</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedStatus(status)
                      setShowStatus(true)
                      setShowError(false)
                    }}
                  >
                    View Full Card
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* All Errors Grid */}
        <Card>
          <CardHeader>
            <CardTitle>All Error Codes (Quick Preview)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {ALL_ERRORS.map((error) => (
                <div key={error} className="border rounded-lg p-4">
                  <h3 className="font-semibold mb-2">{error}</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedError(error)
                      setShowStatus(false)
                      setShowError(true)
                    }}
                  >
                    View Full Card
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

